/* RESPONSIBLE TEAM: team-ai-agent-2 */

import Service, { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import {
  PlaygroundSummary,
  type PlaygroundSummaryWireFormat,
} from 'embercom/lib/fin-playground/playground-summary';
import { type ExtractionType } from 'embercom/lib/fin-playground/question-generation';
import type IntlService from 'ember-intl/services/intl';
import { postRequest, request, putRequest, deleteRequest } from 'embercom/lib/inbox/requests';
import csvParser, { type ParseResult } from 'papaparse';
import { Promise as EmberPromise } from 'rsvp';
import { PlaygroundGroupsSummary } from 'embercom/lib/fin-playground/playground-groups-summary';
import { tracked } from '@glimmer/tracking';

export default class PlaygroundApiService extends Service {
  @service declare appService: $TSFixMe;
  @service declare intercomEventService: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;

  @tracked groupsSummary: PlaygroundGroupsSummary | null = null;

  async parseCsvFile(file: any): Promise<ParseResult<any>> {
    // the CSV parser uses callbacks, so this is just a shim so we can use it as a Promise
    return new EmberPromise((resolve, reject) => {
      let complete = (results: any) => {
        if (results.errors.length) {
          // An UndetectableDelimiter happens with single column CSV files (because no line contains a ,)
          if (results.errors.any((error: any) => error.code !== 'UndetectableDelimiter')) {
            reject(results);
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { complete, error: reject });
    });
  }

  async fetchGroupsSummary(): Promise<void> {
    let response = await request(`/ember/fin_playground_group?app_id=${this.appService.app.id}`);
    let json = await response.json();
    this.groupsSummary = PlaygroundGroupsSummary.deserialize(json);
  }

  async deletePlaygroundGroup(groupId: number) {
    await deleteRequest(`/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`);
  }

  async createPlaygroundGroup(
    groupName: string,
    groupDescription: string,
    selectedContentSegments: string[],
    brandId?: string,
    extractionType?: ExtractionType,
    questions?: string[],
  ) {
    let createdGroupSummary = await taskFor(this._createPlaygroundGroupTask).perform(
      groupName,
      groupDescription,
      selectedContentSegments,
      brandId,
      extractionType,
      questions,
    );

    return createdGroupSummary;
  }

  async updatePlaygroundGroup(groupId: number, groupName: string, groupDescription: string) {
    let updatedGroupSummary = await taskFor(this._updatePlaygroundGroupTask).perform(
      groupId,
      groupName,
      groupDescription,
    );

    return updatedGroupSummary;
  }

  async validateAdminPermissions() {
    await request(
      `/ember/fin_playground/validate_admin_permissions?app_id=${this.appService.app.id}`,
    );
  }

  @dropTask
  *_createPlaygroundGroupTask(
    groupName: string,
    groupDescription: string,
    selectedContentSegments: string[],
    brandId?: string,
    extractionType?: ExtractionType,
    questions?: string[],
  ) {
    let response = (yield postRequest(
      `/ember/fin_playground_group?app_id=${this.appService.app.id}`,
      {
        group_name: groupName,
        group_description: groupDescription,
        selected_content_segments: selectedContentSegments ?? [],
        brand_id: brandId ?? null,
        extraction_type: extractionType ?? null,
        questions: questions?.map((q) => ({ question_text: q })),
      },
    )) as Response;

    let json = (yield response.json()) as PlaygroundSummaryWireFormat;
    return PlaygroundSummary.deserialize(json);
  }

  @dropTask
  *_updatePlaygroundGroupTask(groupId: number, groupName: string, groupDescription: string) {
    let response = (yield putRequest(
      `/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`,
      {
        group_name: groupName,
        group_description: groupDescription,
      },
    )) as Response;

    let json = (yield response.json()) as PlaygroundSummaryWireFormat;
    return PlaygroundSummary.deserialize(json);
  }

  @dropTask
  *_deletePlaygroundGroupTask(groupId: number) {
    yield deleteRequest(`/ember/fin_playground_group/${groupId}?app_id=${this.appService.app.id}`);
  }
}

declare module '@ember/service' {
  interface Registry {
    'playground-api': PlaygroundApiService;
  }
}
